import IosShareIcon from 'assets/images/graphic-ios-share-icon.svg';
import InstallNotificationMoreIcon from 'assets/images/graphic-more-icon.svg';

export const howItWorksPageContent = {
  howItWorks: {
    id: 'how-it-works',
    title: 'How it works',
    subtitle: '',
    content: [
      'Eat at Zizzi and show your ID to earn Zs.',
      'Swap Zs for Perkz.',
      'Use Perkz to get free food.',
    ],
  },
  faqs: {
    id: 'faqs',
    title: 'FAQs',
    subtitle: 'Got a question? Here are your answers.',
    content: [
      {
        name: 'General',
        questions: [
          {
            question: 'Is there an app?',
            answer: `No, but you can add the Zillionaires' Club to your mobile home screen (see question below). Make sure you tick the 'keep me signed in' option on the login page so you can access your account more easily.`,
          },
          {
            question: `How do I add the Zillionaires' Club to my home screen?`,
            answer: `If you are on an iOS mobile device, make sure you're on the Zillionaires' Club website then simply tap the share icon ![iOS share icon](${IosShareIcon}#IosShareIcon) followed by 'Add to Home Screen'. \n
If you are on an Android mobile device, make sure you're on the Zillionaires' Club website, then tap the screen's more icon ![Android more icon](${InstallNotificationMoreIcon}#MoreIcon) at the top right hand corner. Select 'Add to Home screen'.`,
          },
          {
            question: 'How do I stay logged in?',
            answer: "Please make sure you check the 'keep me signed in' option on the login page.",
          },
          {
            question: 'How do I get my free Welcome Perk?',
            answer: `Your Welcome Perk will normally appear in your Perkz Wallet within 15 minutes of signing up to Zillionaires' Club, but in some cases it can take up to 24 hours to appear.`,
          },
          {
            question: `How often can I earn with my Zillionaires' Club?`,
            answer: `You can visit us, use Click & Collect or order takeaway as many times as you like, but there's a cap of two transactions per day, with a minimum of 3 hours between each transaction.`,
          },
        ],
      },
      {
        name: 'Zs',
        questions: [
          {
            question: 'What are Zs?',
            answer:
              'Zs are points that you earn and can swap for Perkz like free food and drink (yes, really!).',
          },
          {
            question: 'How do I earn Zs?',
            answer: `Earning Zs is as easy as 1, 2, 3. You can earn Zs every time you visit and make a transaction at a Zizzi restaurant, Click & Collect or Takeaway through one of our partners. \n
**When dining at a Zizzi restaurant**, make sure you show your Zillionaire ID to your server to register the visit and collect your Zs. \n
**When ordering via Click & Collect or Takeaway**, you can scan the QR code on your Zizzi paper receipt to claim your Zs. For more info, head [here](https://zillionaires.zizzi.co.uk/claim-zs).`,
          },
          {
            question: 'Is there a minimum spend when I visit to earn Zs?',
            answer:
              'You must spend a minimum of £10 / €10 (after any Perkz, offers or vouchers have been redeemed) on each visit or when ordering takeaway or Click & Collect.',
          },
          {
            question: 'How long are my Zs valid for?',
            answer:
              'Zs will expire after 12 months. This is calculated from the date you earned the Zs.',
          },
          {
            question: 'I forgot to scan my Zillionaire ID. What do I do?',
            answer:
              'If you forgot to scan your Zillionaire ID when dining in the restaurant, you can scan the QR code on your paper receipt to claim your Zs.',
          },
          {
            question: 'How long does it take to receive my Zs?',
            answer: 'It may take up to 72 hours for Zs to show up in your account after you visit.',
          },
          {
            question: 'Why did I not receive my Zs after visiting?',
            answer: `Please make sure you scan your Zillionaire ID when visiting to collect your Zs. It may take up to 72 hours for Zs to show up in your account after you visit. \n
If after this time you have still not received your Zs, please contact us [here](https://www.zizzi.co.uk/contact-zizzi-hq) with your Zillionaire ID and order number.`,
          },
          {
            question: 'Can I earn Zs through Click & Collect and Takeaway?',
            answer: `Yes you can! Simply scan the QR code on your Zizzi order receipt, or enter your unique order code [here](https://zillionaires.zizzi.co.uk/claim-zs). Missing your receipt? Please contact us [here](https://www.zizzi.co.uk/contact-zizzi-hq) with your Zillionaire ID and order number.`,
          },
        ],
      },
      {
        name: 'Perkz',
        questions: [
          {
            question: 'Where can I find my Perkz?',
            answer: `You can find your Perkz by heading to the '[Perkz Wallet](https://zillionaires.zizzi.co.uk/perkz-wallet)' page. It can take up to 15 minutes for your Perkz to appear in your Perkz Wallet.`,
          },
          {
            question: 'How do I use my Perkz?',
            answer: `Simply click on the Perk you like, then click 'Purchase Perk'. You'll then receive a unique code - just show this to your server when ordering. It's as simple as that!`,
          },
          {
            question: 'Can I use more than one Perk at once?',
            answer: `You can redeem multiple Perkz at once. Just show each Perk code to your server to redeem.`,
          },
          {
            question: 'Can I redeem my Perkz on Click & Collect and Takeaway?',
            answer: `You can't redeem Perkz on Click & Collect or Takeaway.`,
          },
          {
            question: 'What restrictions are there on Perkz?',
            answer: `Please refer to each Perk for their specific T&Cs.`,
          },
          {
            question: 'When will my Perkz expire?',
            answer: `The Perkz in your Perkz Wallet will expire 12 weeks from date of purchase, unless limited-time promotion T&Cs state otherwise.`,
          },
          {
            question: 'What is a Surprise Perk?',
            answer: `A Surprise Perk is exactly that. After every 4th visit, we'll surprise you with a free random Perk that will be added magically to your Perkz Wallet.`,
          },
          {
            question: 'How can I redeem my Surprise Perk?',
            answer: `Simply head to the Perkz Wallet, select your Surprise Perk and show your Perk Code to a member of the team in the restaurant.`,
          },
        ],
      },
      {
        name: 'Visits',
        questions: [
          {
            question: 'How many Zs will I get for each visit?',
            answer: `Here's how it works: \n
**Restaurant Visits** \n
1st visit = earn 10 Zs \n
2nd visit = earn 20 Zs \n
3rd visit = earn 30 Zs \n
4th visit = earn 40 Zs + get a Surprise Perk \n
**Click & Collect and Takeaway** = 10 Zs.`,
          },
          {
            question: 'What happens after my 4th visit?',
            answer:
              'After your 4th visit, you will earn yourself a free Surprise Perk. After this, you will restart the cycle, earning 10 Zs on your next visit.',
          },
          {
            question: 'When do I get my Surprise Perk?',
            answer: 'You will get a free Surprise Perk after every 4th visit.',
          },
          {
            question: "If I don't visit a Zizzi regularly enough, will I lose my Zs?",
            answer:
              'You must visit and make a qualifying transaction at a Zizzi restaurant, scan your Zillionaire ID and spend a minimum of £10 / €10 on each transaction four times within one year, or the cycle will reset to 0 visits. The cycle begins from when you make your 1st visit. Your Zs will remain in your balance for 12 months from the date they were earned.',
          },
          {
            question: 'How long does it take for my visit to display?',
            answer:
              'After you visit a restaurant, it may take up to 72 hours for your visit to display in your account.',
          },
          {
            question: 'Do I earn a visit when I order via Click & Collect or Takeaway?',
            answer:
              'You earn 10 Zs when making a qualifying transaction via Click & Collect and Takeaway. Ordering via these channels does not count towards your total visits.',
          },
        ],
      },
      {
        name: 'Takeaway',
        questions: [
          {
            question: 'How do I claim my Zs when I order via Click & Collect or Takeaway?',
            answer:
              'Simply scan the QR code on your Zizzi order receipt, or enter your unique order code [here](https://zillionaires.zizzi.co.uk/claim-zs). Missing your receipt? Please contact us [here](https://www.zizzi.co.uk/contact-zizzi-hq) with your Zillionaire ID and order number.',
          },
          {
            question: `How do I get my Zillionaires' Club discount when ordering via Click & Collect?`,
            answer: `To get your exclusive 25% off Zillionaires' Club discount via Click & Collect, please enter your Zillionaire ID at checkout. Please make sure the email address matches the same email address of your Zillionaires' Club account.`,
          },
        ],
      },
      {
        name: 'Refer And Earn',
        questions: [
          {
            question: 'How do I refer a friend?',
            answer:
              'Simply head to the Profile page (this can be found by clicking the top right-hand corner of your account home screen), then click Refer and Earn. Follow the instructions on how to share your code with your family and friends.',
          },
          {
            question: 'What do I receive once I refer a friend?',
            answer: `You will receive 50 Zs for every friend you refer who signs up to the Zillionaires' Club, successfully scans their Zillionaire ID in-restaurant and spends a minimum of £10 / €10 on their visit.`,
          },
          {
            question: 'Who can I refer to earn rewards?',
            answer: `You can only refer those who have not already signed up to the Zillionaires' Club.`,
          },
          {
            question: 'When will I get my referral Zs?',
            answer: `Once the friend has completed the registration process where they will need to enter the referral code sent to them, they will then need to successfully scan their Zillionaire ID in-restaurant and spend a minimum of £10 / €10 on their visit. Once they have done this, 50 Zs will be added to your account within 72 hours.`,
          },
          {
            question: 'Where can I find my referral code / referral link?',
            answer:
              'You can find your referral code / referral link on the Refer & Earn page on your account.',
          },
          {
            question: 'Where can friends enter my referral code / referral link?',
            answer: `They can enter the code in the 'Referral code' box when signing up to the Zillionaires' Club.`,
          },
          {
            question: 'What if a friend signs up but forgets to use my link or code?',
            answer: `Your friend **must** input your referral code at the time of registering for an account. Otherwise you won't be eligible for receiving your referral reward.`,
          },
          {
            question: 'How many friends can I refer?',
            answer: 'You can refer as many friends as you like! Go crazy.',
          },
          {
            question: 'How will I receive my referral Zs?',
            answer:
              'You will receive a notification on the home screen of your account informing you that your friend has made a visit and that you have received your 50 Zs. These Zs will be added automatically to your Z Balance. You will also receive an email informing them that the referral Zs have been earned.',
          },
          {
            question: 'How can I tell if a friend has used my code?',
            answer:
              'Once your friend has completed all the steps listed above, you will receive a confirmation email and notification in the web-app about the Zs being added to your account.',
          },
          {
            question: 'Is there a limit to the number of referral Zs I can earn?',
            answer: `Nope - you can refer as many people as you like and there's no cap on how many Zs you can earn!`,
          },
          {
            question: `Help! My friend has followed all these steps and I still haven't received my referral Zs yet.`,
            answer: `Your Zs may take up to 72 hours after their visit to reflect in your account. If after then you still haven't received your Zs, please contact our Customer Experience Team at [feedback@zizzi.co.uk](mailto:feedback@zizzi.co.uk).`,
          },
          {
            question:
              'Will I still receive 50 referral Zs if my friend orders Delivery or Click & Collect?',
            answer:
              'No, they will need to make a visit to a Zizzi restaurant and spend a minimum of £10 / €10 in order for you to receive your Zs.',
          },
          {
            question: 'When do these referral Zs expire?',
            answer:
              'The Zs you earn through Refer & Earn will expire 12 months from the date you earned them.',
          },
        ],
      },
      {
        name: 'Extended FAQs',
        questions: [
          {
            question: 'Why are some Perkz locked?',
            answer: `You might see that some Perkz cannot be purchased. This simply means you don't have enough Zs yet :(\n\n Keep visiting or order takeaway to earn more Zs!`,
          },
          {
            question: 'Can I use other offers with my Perkz?',
            answer: `Perkz cannot be used in conjunction with any other offers including: set menus, Tesco Clubcard Reward Partner codes, Tastecard, Blue Light Card or student discount. Please refer to individual Perk T&Cs before purchasing and redeeming a Perk.`,
          },
          {
            question: 'Can I earn Zs when using an offer or Tesco Clubcard Reward Partner codes?',
            answer: `You can earn Zs in conjunction with other offers or Tesco Clubcard Reward Partner codes. You must spend a minimum of £10 / €10 (after any Perkz, offers or vouchers have been redeemed) on each visit to earn these.`,
          },
          {
            question: 'Can I use my Perkz alongside Tesco Clubcard Reward Partner codes?',
            answer:
              'Your Perkz cannot be used in conjunction with Tesco Clubcard Reward Partner codes.',
          },
          {
            question: 'I still have a question. How do I contact you?',
            answer: `You can contact us [here](https://www.zizzi.co.uk/contact-zizzi-hq). Please provide your Zillionaire ID and email address registered to your account so we can assist you quickly.`,
          },
          {
            question: 'How do I delete my account?',
            answer:
              'No problem. You can do this by contacting us at [privacy@zizzi.co.uk](mailto:privacy@zizzi.co.uk).',
          },
          {
            question: 'How are you handling my personal information (GDPR)?',
            answer:
              'Your data is held in line with our Privacy Policy, you can read more about it [here](https://www.zizzi.co.uk/privacy-policy).',
          },
        ],
      },
    ],
  },
};
